import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import './UserProfile.css';

const UserProfile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [editMode, setEditMode] = useState(false);
  const [fullName, setFullName] = useState('');
  const [passwordMode, setPasswordMode] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Referral states
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [invitationSuccess, setInvitationSuccess] = useState(false);

  // Load user data and referral stats
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const token = authService.getToken();
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (data.success) {
          setUser(data.user);
          setFullName(data.user.fullName);

          // Fetch referral stats
          try {
            const referralResponse = await authService.getReferralStats();
            if (referralResponse) {
              setReferralCode(referralResponse.referralCode);
              setReferralStats(referralResponse);
            }
          } catch (err) {
            console.error('Error loading referral stats:', err);
          }
        } else {
          setError('Failed to load profile');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Error loading profile');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);

  // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = authService.getToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/profile/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: fullName,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Profile updated successfully!');
        setUser(data.user);
        setEditMode(false);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Error updating profile');
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const token = authService.getToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oldPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Password changed successfully!');
        setPasswordMode(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (err) {
      setError('Error changing password');
    }
  };

  // Copy referral code
  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Send invitation
  const handleSendInvitation = async (e) => {
    e.preventDefault();
    setInvitationError('');
    setInvitationSuccess(false);

    if (!inviteEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setInvitationError('Please enter a valid email address');
      return;
    }

    setSendingInvitation(true);

    try {
      const response = await authService.sendReferralInvitation(inviteEmail);

      if (response.success) {
        setInvitationSuccess(true);
        setInviteEmail('');

        // Refresh referral stats
        const updatedStats = await authService.getReferralStats();
        if (updatedStats) {
          setReferralStats(updatedStats);
        }
      } else {
        setInvitationError(response.message || 'Failed to send invitation');
      }
    } catch (err) {
      setInvitationError(err.message || 'Error sending invitation');
    } finally {
      setSendingInvitation(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container-gh">
        <nav className="top-nav">
          <div className="nav-container">
            <div className="nav-left">
              <h1 className="app-logo">AI Recruitment System</h1>
            </div>
          </div>
        </nav>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container-gh">
      {/* Top Navigation */}
      <nav className="top-nav">
        <div className="nav-container">
          <div className="nav-left">
            <h1 className="app-logo">AI Recruitment System</h1>
          </div>

          <div className="nav-right">
            <button
              className="nav-link"
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </button>
            <button
              className="user-profile-btn active"
              title="Profile"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
            <button
              className="logout-btn"
              onClick={() => {
                localStorage.clear();
                window.location.href = '/login';
              }}
              title="Logout"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="profile-content-wrapper">
        <div className="profile-header-gh">
          <h1>Account Settings</h1>
          <p>Manage your profile and security settings</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-gh alert-error-gh">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="alert-gh alert-success-gh">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            {success}
          </div>
        )}

        {/* Profile Card */}
        <div className="profile-card-gh">
          <div className="card-header-gh">
            <h2>Profile Information</h2>
            {!editMode && !user?.authProvider && (
              <button onClick={() => setEditMode(true)} className="btn-secondary-gh">
                Edit Profile
              </button>
            )}
          </div>

          {editMode ? (
            <form onSubmit={handleUpdateProfile} className="profile-form-gh">
              <div className="form-group-gh">
                <label>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="form-actions-gh">
                <button type="button" onClick={() => setEditMode(false)} className="btn-ghost">
                  Cancel
                </button>
                <button type="submit" className="btn-primary-gh">
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            <div className="profile-info-gh">
              <div className="info-row-gh">
                <span className="info-label">Full Name</span>
                <span className="info-value">{user?.fullName}</span>
              </div>
              <div className="info-row-gh">
                <span className="info-label">Email Address</span>
                <span className="info-value">{user?.email}</span>
              </div>
              <div className="info-row-gh">
                <span className="info-label">Account Status</span>
                <span className="status-badge-gh status-active-gh">Active</span>
              </div>
              <div className="info-row-gh">
                <span className="info-label">Email Verified</span>
                <span className="status-badge-gh status-verified-gh">Verified</span>
              </div>
              {user?.authProvider && (
                <div className="info-row-gh">
                  <span className="info-label">Login Method</span>
                  <span className="info-value">🔑 {user.authProvider === 'google' ? 'Google' : 'Email & Password'}</span>
                </div>
              )}
            </div>
          )}

          {user?.authProvider === 'google' && (
            <div className="info-box-gh info-box-blue">
              <p><strong>Google Account</strong></p>
              <p>Your account is linked with Google. Profile updates should be managed through your Google account settings.</p>
            </div>
          )}
        </div>

        {/* Password Change (only for email users) */}
        {!user?.authProvider && (
          <div className="profile-card-gh">
            <div className="card-header-gh">
              <h2>Security</h2>
              {!passwordMode && (
                <button onClick={() => setPasswordMode(true)} className="btn-secondary-gh">
                  Change Password
                </button>
              )}
            </div>

            {passwordMode ? (
              <form onSubmit={handleChangePassword} className="profile-form-gh">
                <div className="form-group-gh">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group-gh">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group-gh">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="form-actions-gh">
                  <button type="button" onClick={() => setPasswordMode(false)} className="btn-ghost">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary-gh">
                    Update Password
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-box-gh info-box-green">
                <p>Keep your account secure by using a strong password and changing it regularly.</p>
              </div>
            )}
          </div>
        )}

        {/* Referral Program */}
        {referralStats && (
          <div className="profile-card-gh referral-card-gh">
            <div className="card-header-gh">
              <h2>🎁 Referral Program</h2>
            </div>

            <div className="referral-content-gh">
              <p className="referral-description-gh">
                Invite friends to join and help grow our community!
              </p>

              {/* Invite via Email */}
              <div className="invite-section-gh">
                <h3>✉️ Invite Friends</h3>
                <p className="invite-description">Enter your friend's email and we'll send them a signup link with your referral</p>

                <form onSubmit={handleSendInvitation} className="invite-form-gh">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                    disabled={sendingInvitation || referralStats.remainingReferrals === 0}
                  />
                  <button
                    type="submit"
                    className="btn-primary-gh"
                    disabled={sendingInvitation || referralStats.remainingReferrals === 0}
                  >
                    {sendingInvitation ? 'Sending...' : '📤 Send Invite'}
                  </button>
                </form>

                {invitationError && (
                  <div className="alert-gh alert-error-gh">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    {invitationError}
                  </div>
                )}

                {invitationSuccess && (
                  <div className="alert-gh alert-success-gh">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    Invitation sent successfully!
                  </div>
                )}
              </div>

              {/* Referral Stats */}
              <div className="referral-stats-gh">
                <div className="stat-item-gh">
                  <span className="stat-value-gh">{referralStats.referralCount || 0}</span>
                  <span className="stat-label-gh">Referrals Made</span>
                </div>
                <div className="stat-item-gh">
                  <span className="stat-value-gh">{referralStats.remainingReferrals}</span>
                  <span className="stat-label-gh">Remaining</span>
                </div>
              </div>

              <div className="info-box-gh info-box-teal">
                <p>📢 Maximum: 5 referrals per user. Invite links expire after 7 days.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;